import { type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import {
  type Address,
  type ContractFunctionReturnType,
  type ContractFunctionName,
  type ContractFunctionArgs,
  type ReadContractReturnType,
} from 'viem';
import { useCofheChainId, useCofhePublicClient } from './useCofheConnection';
import { useCofheActiveACP } from './useCofheACPs';
import { assert } from 'ts-essentials';
import { useInternalQuery } from '../providers/index';
import { transformEncryptedReturnTypes, type Abi, type CofheReturnType, type ContractReturnType } from '@cofhe/abi';
import { serializeBigintRecursively } from '../utils/serializeBigint.js';
import { maybeWaitUntilRpcAwareAndReadContract } from '@/utils/waitUntilRpcAwareAndReadContract';
import { withInvalidationContext } from '@/utils/invalidationContext';

const QUERY_CACHE_PREFIX = 'cofheReadContract';

export function constructCofheReadContractQueryKey({
  cofheChainId,
  address,
  functionName,
  args,
  requiresACP,
  activeACPHash,
  enabled,
}: {
  cofheChainId?: number;
  address?: Address;
  functionName?: string;
  args?: readonly unknown[];
  requiresACP?: boolean;
  activeACPHash?: string;
  enabled?: boolean;
}): readonly unknown[] {
  return [
    ...constructCofheReadContractQueryForInvalidation({
      cofheChainId,
      address,
      functionName,
    }),

    args ? serializeBigintRecursively(args) : [],
    requiresACP ? activeACPHash : undefined,
    // normally, "enabled" shouldn't be part of queryKey, but without adding it, there is a weird bug: when there's a CofheError, query still running queryFn resulting in the blank screen
    enabled,
  ];
}

export function constructCofheReadContractQueryForInvalidation({
  cofheChainId,
  address,
  functionName,
}: {
  cofheChainId?: number;
  address?: Address;
  functionName?: string;
  // add more specificity if needed. Just make sure it matches the order of keys
}): readonly unknown[] {
  return [QUERY_CACHE_PREFIX, cofheChainId, address, functionName];
}

export type UseCofheReadContractQueryOptions<
  TAbi extends Abi,
  TfunctionName extends ContractFunctionName<TAbi, 'pure' | 'view'>,
> = Omit<UseQueryOptions<CofheReturnType<TAbi, TfunctionName>, Error>, 'queryKey' | 'queryFn'> & {
  enabled?: boolean; // TODO: check callback variant, maybe it'll fix the issue above about forcing enable to be query key
};

export function getEnabledForCofheReadContract(params: {
  publicClient: unknown;
  address?: Address;
  abi?: Abi;
  functionName?: string;
  requiresACP: boolean;
  hasActiveACP: boolean;
  userEnabled?: boolean;
}): boolean {
  const { publicClient, address, abi, functionName, requiresACP, hasActiveACP, userEnabled } = params;

  return (
    !!publicClient && !!address && !!abi && !!functionName && (!requiresACP || hasActiveACP) && (userEnabled ?? true)
  );
}

function convertReadContractResultToCofheReturnType<
  TAbi extends Abi,
  TfunctionName extends ContractFunctionName<TAbi, 'pure' | 'view'>,
  TArgs extends ContractFunctionArgs<TAbi, 'pure' | 'view', TfunctionName>,
>(
  value: ContractFunctionReturnType<TAbi, 'pure' | 'view', TfunctionName, TArgs>
): ContractReturnType<
  TAbi,
  TfunctionName
  //TArgs
> {
  // TODO: convertViemReturnTypeToCofheReturnType -- need core typing changes, currently seems to not support fn overloads
  // viems inferred TArgs mismatch Cofhe's
  return value as ContractReturnType<
    TAbi,
    TfunctionName
    //TArgs
  >;
}

export function createCofheReadContractQueryOptions<
  TAbi extends Abi,
  TfunctionName extends ContractFunctionName<TAbi, 'pure' | 'view'>,
  TArgs extends ContractFunctionArgs<TAbi, 'pure' | 'view', TfunctionName>,
>(params: {
  enabled: boolean;
  cofheChainId?: number;
  address?: Address;
  abi?: TAbi;
  functionName?: TfunctionName;
  args?: TArgs;
  requiresACP: boolean;
  activeACPHash?: string;
  publicClient: ReturnType<typeof useCofhePublicClient>;
  queryOptions?: UseCofheReadContractQueryOptions<TAbi, TfunctionName>;
}): UseQueryOptions<CofheReturnType<TAbi, TfunctionName>, Error> {
  const {
    enabled,
    cofheChainId,
    address,
    abi,
    functionName,
    args,
    requiresACP,
    activeACPHash,
    publicClient,
    queryOptions,
  } = params;

  const { enabled: _ignoredEnabled, meta: optionMeta, ...restQueryOptions } = queryOptions || {};

  return {
    enabled,
    // Recognition metadata for observers (debug panels / activity hook): the
    // contract + method, without them having to parse the query key.
    meta: {
      kind: 'cofheRead',
      chainId: cofheChainId,
      address,
      functionName,
      ...optionMeta,
    },
    queryKey: constructCofheReadContractQueryKey({
      cofheChainId,
      address,
      functionName,
      args: Array.isArray(args) ? args : undefined,
      requiresACP,
      activeACPHash,
      enabled,
    }),
    queryFn: withInvalidationContext<
      readonly unknown[],
      { blockHashToBeAwareOf: `0x${string}` },
      CofheReturnType<TAbi, TfunctionName>
    >(async ({ invalidationContext, signal }) => {
      // the invalidationContext matches the current query by key
      assert(address, 'Contract address should be guaranteed by enabled check');
      assert(publicClient, 'PublicClient should be guaranteed by enabled check');
      assert(abi, 'ABI should be guaranteed by enabled check');
      assert(functionName, 'Function name should be guaranteed by enabled check');

      const normalizedArgs = (args ?? []) as ContractFunctionArgs<TAbi, 'pure' | 'view', TfunctionName>;

      const out = await maybeWaitUntilRpcAwareAndReadContract(
        publicClient,
        {
          blockHashToBeAwareOf: invalidationContext?.blockHashToBeAwareOf,
          address,
          abi,
          functionName,
          args: normalizedArgs,
        },
        { signal }
      );

      const convertedOut = convertReadContractResultToCofheReturnType<TAbi, TfunctionName, TArgs>(out);

      const transformed = transformEncryptedReturnTypes(abi, functionName, convertedOut);

      return transformed;
    }),
    ...restQueryOptions,
  };
}

/**
 * Generic hook: read a contract and return the result (with acp/error gating support).
 * is Cofhe-ABI aware: returns CofheReturnType (but doesn't support TArgs typing yet).
 */
export type UseCofheReadContractResult<
  TAbi extends Abi,
  TfunctionName extends ContractFunctionName<TAbi, 'pure' | 'view'>,
> = UseQueryResult<CofheReturnType<TAbi, TfunctionName>, Error> & {
  disabledDueToMissingValidACP: boolean;
};
export function useCofheReadContract<
  TAbi extends Abi,
  TfunctionName extends ContractFunctionName<TAbi, 'pure' | 'view'>,
>(
  params: {
    address?: Address;
    abi?: TAbi;
    functionName?: TfunctionName;
    args?: ContractFunctionArgs<TAbi, 'pure' | 'view', TfunctionName>;
    requiresACP?: boolean;
  },
  queryOptions?: UseCofheReadContractQueryOptions<TAbi, TfunctionName>
): UseCofheReadContractResult<TAbi, TfunctionName> {
  const { address, abi, functionName, args, requiresACP = true } = params;

  const publicClient = useCofhePublicClient();
  const cofheChainId = useCofheChainId();
  const activeACP = useCofheActiveACP();

  const enabled = getEnabledForCofheReadContract({
    publicClient,
    address,
    abi,
    functionName,
    requiresACP,
    hasActiveACP: !!activeACP,
    userEnabled: queryOptions?.enabled,
  });

  const result = useInternalQuery(
    createCofheReadContractQueryOptions({
      enabled,
      cofheChainId,
      address,
      abi,
      functionName,
      args: Array.isArray(args) ? args : undefined,
      requiresACP,
      activeACPHash: activeACP?.acp.hash,
      publicClient,
      queryOptions,
    })
  );

  return {
    ...result,
    disabledDueToMissingValidACP: requiresACP && (!activeACP || !activeACP.isValid),
  };
}
